import { useToast } from '@monorepo/shared/contexts';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import { bodySmall } from '@monorepo/shared/styles/text';
import { FormTemplateAttribute, FormTemplateFieldTypes } from 'mapistry-shared';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { EditFieldConditionsModal } from './EditFieldConditionsModal/EditFieldConditionsModal';
import { EditFieldModal } from './EditFieldModal/EditFieldModal';
import { hasForbiddenHtml } from './helperTextUtil';
import { MoveInspectionDateModal } from './MoveInspectionDateModal/MoveInspectionDateModal';
import { ReorderableFormTemplateFieldsList } from './ReorderableFormTemplateFieldsList';
import { EditableFormTemplateField } from './types';

type EditFormTemplateFieldsProps = {
  isEditingDisabled: boolean;
  isInDuplicableSection: boolean;
  orderedFields: EditableFormTemplateField[];
};

const Instructions = styled('span')`
  ${bodySmall}
`;

export function EditFormTemplateFields({
  isEditingDisabled,
  isInDuplicableSection,
  orderedFields,
}: EditFormTemplateFieldsProps) {
  const { confirm } = useModal();
  const { error: showError, success: showSuccess } = useToast();

  const {
    formTemplate,
    formTemplateHelpers: {
      deleteField,
      copyField,
      fieldConditionHelpers,
      moveInspectionDateToGroup,
      updateField,
      updateFieldOrder,
    },
  } = useFormTemplateContext();

  const [currentlyOpenEditFieldId, setCurrentlyOpenEditFieldId] =
    useState<string>();
  const closeEditFieldModal = useCallback(() => {
    setCurrentlyOpenEditFieldId(undefined);
  }, [setCurrentlyOpenEditFieldId]);
  const currentlyOpenEditField = orderedFields.find(
    (f) => f.id === currentlyOpenEditFieldId,
  );

  const [
    currentlyOpenEditConditionsFieldId,
    setCurrentlyOpenEditConditionsFieldId,
  ] = useState<string>();
  const closeEditFieldConditionsModal = useCallback(() => {
    setCurrentlyOpenEditConditionsFieldId(undefined);
  }, [setCurrentlyOpenEditConditionsFieldId]);
  const currentlyOpenEditConditionsField = orderedFields.find(
    (f) => f.id === currentlyOpenEditConditionsFieldId,
  );

  const [
    isMoveInspectionDateModalOpen,
    openMoveInspectionDateModal,
    closeMoveInspectionDateModal,
  ] = useOpenClose();

  const checkForForbiddenHtml = useCallback(
    async (originalField: EditableFormTemplateField) => {
      if (
        originalField.type !== FormTemplateFieldTypes.EXPLANATION_AREA ||
        !hasForbiddenHtml(originalField.helpText)
      ) {
        return true;
      }

      const confirmSave = await confirm({
        title: 'Saving will overwrite unsupported styles',
        description: (
          <>
            This explanation area was initially built by the Mapistry customer
            experience team and contains style elements unsupported on the
            mobile app, such as font color, font size, tables, etc. If you save
            your changes, these style elements will be erased. Are you sure you
            want to save your changes?
          </>
        ),
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Save',
        danger: true,
      });

      return confirmSave;
    },
    [confirm],
  );

  const onSubmitField = useCallback(
    async (updatedField: EditableFormTemplateField) => {
      const originalField = orderedFields.find(
        (field) => field.id === updatedField.id,
      );

      if (!originalField) return; // for typescript

      const shouldContinue = await checkForForbiddenHtml(originalField);
      if (!shouldContinue) return;

      updateField(updatedField);
      closeEditFieldModal();
      closeEditFieldConditionsModal();
    },
    [
      checkForForbiddenHtml,
      closeEditFieldModal,
      closeEditFieldConditionsModal,
      orderedFields,
      updateField,
    ],
  );

  const onMoveInspectionDate = useCallback(
    (newGroupSlug: string) => {
      const newGroup = formTemplate?.groups.find(
        (group) => group.slug === newGroupSlug,
      );
      if (!newGroup) {
        throw new Error('Could not find the specified section.');
      }
      moveInspectionDateToGroup(newGroupSlug);
      showSuccess(`Inspection date moved to "${newGroup.name}"`);
    },
    [formTemplate?.groups, moveInspectionDateToGroup, showSuccess],
  );

  const onCopyField = useCallback(
    (fieldToCopy: EditableFormTemplateField) => {
      try {
        copyField(fieldToCopy);
        showSuccess(`Copied "${fieldToCopy.name}"`);
      } catch (err) {
        showError((err as Error).message, { autoHideDuration: 10000 });
      }
    },
    [copyField, showError, showSuccess],
  );

  const onDeleteField = useCallback(
    (fieldToDelete: EditableFormTemplateField) => {
      deleteField(fieldToDelete);
      showSuccess(`"${fieldToDelete.name}" has been removed.`);
    },
    [deleteField, showSuccess],
  );

  const getIsMovingDisabledAndReason = useCallback(
    (field: EditableFormTemplateField) => {
      if (formTemplate.groups.length === 1) {
        return {
          isMovingDisabled: true,
          movingDisabledReason:
            'Cannot move sections when there is only one section.',
        };
      }
      if (orderedFields.length === 1) {
        return {
          isMovingDisabled: true,
          movingDisabledReason: 'Sections must have at least one field.',
        };
      }
      if (fieldConditionHelpers.isConditionTriggerField(field.id)) {
        return {
          isMovingDisabled: true,
          movingDisabledReason:
            'This field cannot be moved because other fields are conditionally shown based on the result of this field.',
        };
      }
      return {
        isMovingDisabled: isEditingDisabled,
        movingDisabledReason: '',
      };
    },
    [
      orderedFields.length,
      formTemplate.groups,
      isEditingDisabled,
      fieldConditionHelpers,
    ],
  );

  const getIsDeletingDisabledAndReason = useCallback(
    (field: EditableFormTemplateField) => {
      if (
        field.attributes?.includes(FormTemplateAttribute.IS_INSPECTION_DATE)
      ) {
        return {
          isDeletingDisabled: true,
          deletingDisabledReason:
            'Before deleting, you must choose another date field as the inspection date.',
        };
      }
      if (fieldConditionHelpers.isConditionTriggerField(field.id)) {
        return {
          isDeletingDisabled: true,
          deletingDisabledReason:
            'This field cannot be deleted because it determines the visibility of at least one other field.',
        };
      }
      if (orderedFields.length === 1) {
        return {
          isDeletingDisabled: true,
          deletingDisabledReason: 'Sections must have at least one field.',
        };
      }
      return {
        isDeletingDisabled: isEditingDisabled,
        deletingDisabledReason: '',
      };
    },
    [isEditingDisabled, orderedFields.length, fieldConditionHelpers],
  );

  const isConditionTriggerField =
    !!currentlyOpenEditFieldId &&
    fieldConditionHelpers.isConditionTriggerField(currentlyOpenEditFieldId);

  return (
    <div>
      <Instructions>Reorder or edit the fields in this section.</Instructions>
      {orderedFields && (
        <ReorderableFormTemplateFieldsList
          fieldConditionHelpers={fieldConditionHelpers}
          openEditFieldModal={setCurrentlyOpenEditFieldId}
          openEditFieldConditionsModal={setCurrentlyOpenEditConditionsFieldId}
          openMoveInspectionDateModal={openMoveInspectionDateModal}
          onCopyField={onCopyField}
          onDeleteField={onDeleteField}
          sortedFields={orderedFields}
          updateFieldOrder={updateFieldOrder}
          isEditingDisabled={isEditingDisabled}
          getIsMovingDisabledAndReason={getIsMovingDisabledAndReason}
          getIsDeletingDisabledAndReason={getIsDeletingDisabledAndReason}
        />
      )}
      {!!currentlyOpenEditField && (
        <EditFieldModal
          isInDuplicableSection={isInDuplicableSection}
          isOpen={!!currentlyOpenEditFieldId}
          onClose={closeEditFieldModal}
          onSubmit={onSubmitField}
          field={currentlyOpenEditField}
          isConditionTriggerField={isConditionTriggerField}
        />
      )}
      {!!currentlyOpenEditConditionsField && (
        <EditFieldConditionsModal
          fieldsInGroup={orderedFields}
          isOpen={!!currentlyOpenEditConditionsFieldId}
          onClose={closeEditFieldConditionsModal}
          onSubmit={onSubmitField}
          field={currentlyOpenEditConditionsField}
          fieldConditionHelpers={fieldConditionHelpers}
        />
      )}
      {!!isMoveInspectionDateModalOpen && (
        <MoveInspectionDateModal
          isOpen={!!isMoveInspectionDateModalOpen}
          onClose={closeMoveInspectionDateModal}
          onMoveInspectionDate={onMoveInspectionDate}
        />
      )}
    </div>
  );
}
